import React, { useRef, useEffect, useState } from 'react';
import { EditableTitle } from './EditableTitle';

interface VonMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  modelUsed?: string;
  tokens?: {
    prompt: number;
    completion: number;
    total: number;
  };
  estimatedCostUsd?: number;
  createdAt: string;
}

interface VonSession {
  id: string;
  title: string;
  sessionType: string;
  totalMessages: number;
  lastActivityAt: string;
  createdAt: string;
  isArchived: boolean;
}

interface ChatAreaProps {
  currentSession: VonSession | null;
  messages: VonMessage[];
  message: string;
  onMessageChange: (message: string) => void;
  onSendMessage: () => void;
  isLoading: boolean;
  error: string | null;
  onDismissError: () => void;
  onUpdateTitle: (newTitle: string) => Promise<boolean>;
  onDeleteSession: () => void;
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  } | null;
  isLoadingMore: boolean;
  onLoadMoreMessages: () => void;
}

export const ChatArea: React.FC<ChatAreaProps> = ({
  currentSession,
  messages,
  message,
  onMessageChange,
  onSendMessage,
  isLoading,
  error,
  onDismissError,
  onUpdateTitle,
  onDeleteSession,
  pagination,
  isLoadingMore,
  onLoadMoreMessages
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [shouldPreserveScroll, setShouldPreserveScroll] = useState(false);

  // Reset scroll state when session changes
  useEffect(() => {
    setLastMessageCount(0);
    setShouldPreserveScroll(false);
    // Scroll to bottom for initial load
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }, 100);
  }, [currentSession?.id]);

  // Smart auto-scroll: only scroll for new messages, not pagination loads
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // If we're loading more messages (pagination), preserve scroll position
    if (isLoadingMore) {
      setShouldPreserveScroll(true);
      return;
    }

    // If this is a pagination load completion, restore scroll position
    if (shouldPreserveScroll && messages.length > lastMessageCount) {
      setShouldPreserveScroll(false);
      setLastMessageCount(messages.length);
      return; // Don't auto-scroll
    }

    // For new messages (not pagination), scroll to bottom
    if (messages.length > lastMessageCount && !shouldPreserveScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    setLastMessageCount(messages.length);
  }, [messages, isLoadingMore, shouldPreserveScroll, lastMessageCount]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [message]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (message.trim() && !isLoading) {
        onSendMessage();
      }
    }
  };

  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatCost = (cost: number) => {
    return cost < 0.01 ? '<$0.01' : `$${cost.toFixed(3)}`;
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    onDeleteSession();
    setShowDeleteConfirm(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  if (!currentSession) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Welcome to Von Chat</h3>
          <p className="text-gray-600 mb-6">
            Start a new conversation to explore your data with AI-powered insights. 
            Use neural search and advanced filters to get the most relevant responses.
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span>Neural search enabled</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              <span>Smart model selection</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
              <span>Advanced filtering</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
              <span>Cost tracking</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-white">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <EditableTitle
              title={currentSession.title}
              onSave={onUpdateTitle}
              className="text-lg font-semibold text-gray-900"
            />
            <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
              <span>{pagination?.total || currentSession.totalMessages} message{(pagination?.total || currentSession.totalMessages) !== 1 ? 's' : ''}</span>
              <span>•</span>
              <span>Created {new Date(currentSession.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {isLoading && (
              <div className="flex items-center space-x-2 text-blue-600">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm">Thinking...</span>
              </div>
            )}
            <button
              onClick={handleDeleteClick}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete chat"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-2">
              <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-red-800">Error</h4>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                {error.includes('timeout') && (
                  <div className="mt-2 text-xs text-red-600">
                    <p>💡 <strong>Suggestions:</strong></p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Try using a faster model (A1, A2, A3) from the filter panel</li>
                      <li>Simplify your query or break it into smaller questions</li>
                      <li>Disable neural search temporarily if not needed</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onDismissError}
              className="text-red-500 hover:text-red-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-6 py-4">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <p className="text-gray-600 mb-2">Start the conversation</p>
            <p className="text-sm text-gray-500">Ask anything about your data and I'll help you find insights</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Load More Button */}
            {pagination && pagination.hasMore && (
              <div className="flex justify-center pb-4">
                <button
                  onClick={onLoadMoreMessages}
                  disabled={isLoadingMore}
                  className={`flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors ${
                    isLoadingMore ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isLoadingMore ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                      <span>Load {pagination.limit} more messages</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Message Pagination Info */}
            {pagination && pagination.total > 0 && (
              <div className="text-center text-xs text-gray-500 pb-2">
                Showing {messages.length} of {pagination.total} messages
              </div>
            )}

            {messages.map((msg, index) => (
              <div key={msg.id || index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-3xl ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                  <div className={`flex items-start space-x-3 ${msg.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    {/* Avatar */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      msg.role === 'user' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700'
                    }`}>
                      {msg.role === 'user' ? 'U' : 'AI'}
                    </div>
                    
                    {/* Message Content */}
                    <div className={`flex-1 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                      <div className={`inline-block p-4 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}>
                        <div className="whitespace-pre-wrap">{msg.content}</div>
                      </div>
                      
                      {/* Message Metadata */}
                      <div className={`flex items-center space-x-4 mt-2 text-xs text-gray-500 ${
                        msg.role === 'user' ? 'justify-end' : 'justify-start'
                      }`}>
                        <span>{formatTimestamp(msg.createdAt)}</span>
                        {msg.tokens && (
                          <>
                            <span>•</span>
                            <span>{msg.tokens.total.toLocaleString()} tokens</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Limit Warning */}
      {(() => {
        const MESSAGE_LIMIT = 100;
        const WARNING_THRESHOLD = 90;
        const messageCount = pagination?.total || currentSession?.totalMessages || 0;
        
        if (messageCount >= MESSAGE_LIMIT) {
          return (
            <div className="px-6 py-3 bg-red-50 border-t border-red-200">
              <div className="flex items-center space-x-2 text-red-800">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium">Message limit reached</p>
                  <p className="text-xs text-red-600">This conversation has reached the maximum of {MESSAGE_LIMIT} messages. Start a new chat to continue.</p>
                </div>
              </div>
            </div>
          );
        } else if (messageCount >= WARNING_THRESHOLD) {
          const remaining = MESSAGE_LIMIT - messageCount;
          return (
            <div className="px-6 py-3 bg-amber-50 border-t border-amber-200">
              <div className="flex items-center space-x-2 text-amber-800">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium">Approaching message limit</p>
                  <p className="text-xs text-amber-600">{remaining} messages remaining before the {MESSAGE_LIMIT} message limit.</p>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Input Area */}
      <div className="px-6 py-4 border-t border-gray-200 bg-white">
        {(() => {
          const MESSAGE_LIMIT = 100;
          const isAtLimit = (pagination?.total || currentSession?.totalMessages || 0) >= MESSAGE_LIMIT;
          
          return (
            <div className="flex items-end space-x-4">
              <div className="flex-1">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => onMessageChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={isAtLimit ? "Message limit reached - start a new chat" : "Ask anything about your data..."}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent resize-none ${
                    isAtLimit 
                      ? 'border-red-300 bg-red-50 text-red-600 placeholder-red-400 cursor-not-allowed'
                      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                  rows={1}
                  style={{ minHeight: '48px', maxHeight: '200px' }}
                  disabled={isLoading || isAtLimit}
                />
              </div>
              <button
                onClick={onSendMessage}
                disabled={!message.trim() || isLoading || isAtLimit}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  !message.trim() || isLoading || isAtLimit
                    ? 'opacity-50 cursor-not-allowed bg-gray-400 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          );
        })()}
        
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
            <div>Press Enter to send, Shift+Enter for new line</div>
            <div className="flex items-center space-x-4">
              <span>{pagination?.total || currentSession?.totalMessages || 0}/100 messages</span>
              <span>•</span>
              <span>Neural search enabled</span>
              <span>•</span>
              <span>Auto model selection</span>
            </div>
          </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Delete Chat</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete "<strong>{currentSession.title}</strong>"? 
              All messages in this conversation will be permanently removed.
            </p>
            
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors"
              >
                Delete Chat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 